import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { getComicBySlug } from "@/mocks/comics";
import { articleContent } from "@/mocks/comics";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<"chapters" | "info">("chapters");

  const comic = slug ? getComicBySlug(slug) : null;
  const detail = articleContent;

  if (!comic) {
    return (
      <div className="pt-16">
        <div className="w-full px-4 md:px-6 py-20">
          <div className="max-w-7xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${comic.title} - 免费韩漫在线阅读`}
        description={`${comic.description.substring(0, 150)}`}
        keywords={`${comic.title}, 韩漫, 韩国漫画, 免费漫画, 韩漫之家`}
        canonicalUrl={`/article/${comic.slug}`}
        ogType="article"
        ogImage={comic.cover}
        articlePublishedTime="2025-03-15"
        articleModifiedTime="2026-08-05"
      />

      <div className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: detail.category, href: `/category/${detail.categorySlug}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="aspect-[3/4] overflow-hidden rounded-lg">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                  <span className="text-sm text-foreground-600">分类：{detail.category}</span>
                  <span className="text-sm text-foreground-600">状态：{comic.status}</span>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-yellow-500"></i>
                    <span className="font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-foreground-500">分</span>
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {comic.views} 次浏览
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-heart-line mr-1"></i>
                    {detail.favorites} 收藏
                  </span>
                  <span className="text-sm text-foreground-500">
                    共 {comic.chapters} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {detail.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读第一话
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-primary-500 transition-colors cursor-pointer"
                    aria-label="收藏"
                  >
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button
                    type="button"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-primary-500 transition-colors cursor-pointer"
                    aria-label="分享"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <div className="flex items-center border-b border-background-200 mb-6">
                <button
                  type="button"
                  onClick={() => setActiveTab("chapters")}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === "chapters"
                      ? "border-primary-500 text-primary-500"
                      : "border-transparent text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  章节列表
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("info")}
                  className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === "info"
                      ? "border-primary-500 text-primary-500"
                      : "border-transparent text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  作品介绍
                </button>
              </div>

              {activeTab === "chapters" && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-foreground-600">
                      共 {comic.chapters} 话 · 更新至 {comic.updatedAt}
                    </p>
                    <button
                      type="button"
                      className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-sort-desc mr-1"></i>
                      倒序
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {detail.chapters.map((chapter) => (
                      <div
                        key={chapter.number}
                        className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/20 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">
                            {chapter.title}
                          </span>
                          {chapter.isFree && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-green-100 text-green-700 rounded">免费</span>
                          )}
                        </div>
                        <span className="text-xs text-foreground-400 flex-shrink-0">{chapter.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "info" && (
                <div className="prose prose-sm max-w-none">
                  <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                    {detail.longDescription.split("\n\n").map((paragraph, i) => (
                      <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: comic.title,
              description: comic.description,
              author: { "@type": "Person", name: comic.author },
              datePublished: "2025-03-15",
              dateModified: "2026-08-05",
              image: comic.cover,
              publisher: {
                "@type": "Organization",
                name: "韩漫之家",
              },
            }),
          }}
        />
      </div>
    </>
  );
}